import React, { useState, useEffect } from 'react'
import {
  Box,
  Tabs,
  Tab,
  Container,
  Paper,
  useTheme,
  alpha,
} from '@mui/material'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Business as SitesIcon,
  MeetingRoom as RoomsIcon,
  AccountTree as UnitsIcon,
} from '@mui/icons-material'

const tabsConfig = [
  {
    label: 'Sites',
    icon: <SitesIcon sx={{ mr: 1 }} />,
    route: '/m/facility/sites',
    description: 'Gérer les sites médicaux',
  },
  {
    label: 'Unités Fonctionnelles',
    icon: <UnitsIcon sx={{ mr: 1 }} />,
    route: '/m/facility/functional-units',
    description: 'Gérer les unités fonctionnelles',
  },
  {
    label: 'Salles',
    icon: <RoomsIcon sx={{ mr: 1 }} />,
    route: '/m/facility/rooms',
    description: 'Gérer les salles et espaces',
  },
  {
    label: 'Organisations',
    route: '/m/facility/organisations',
    description: 'Gérer les organisations',
  },
]

export default function FacilityManagement() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()

  const getTabValue = () => {
    const path = location.pathname
    if (path.includes('/sites')) return 0
    if (path.includes('/functional-units')) return 1
    if (path.includes('/rooms')) return 2
    if (path.includes('/organisations')) return 3
    return 0
  }

  const [value, setValue] = useState(getTabValue())

  useEffect(() => {
    setValue(getTabValue())
  }, [location.pathname])

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
    navigate(tabsConfig[newValue].route)
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        backgroundColor: theme.palette.grey[50],
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={1}
        sx={{
          backgroundColor: 'white',
          borderRadius: 0,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth={false}>
          {/* Enhanced Tabs */}
          <Tabs
            value={value}
            onChange={handleChange}
            sx={{
              '& .MuiTab-root': {
                minHeight: 56,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                color: theme.palette.text.secondary,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              },
            }}
          >
            {tabsConfig.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                sx={{
                  px: 2,
                  '& .MuiTab-iconWrapper': {
                    mb: 0,
                  },
                }}
              />
            ))}
          </Tabs>
        </Container>
      </Paper>

      {/* Content Section */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            height: '100%',
            '& > *': {
              height: '100%',
            },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  )
}
